import { HandHeart, Leaf, Hand, Users, Heart } from "lucide-react";
import IvyVine from "@/components/IvyVine";
import { FlowerPink, FlowerRed, FlowerBlue, FlowerYellow, FlowerLavender, LeafSprig } from "@/components/FlowerDeco";

const values = [
  {
    icon: HandHeart,
    title: "Faith Driven",
    body: "We honor God in everything we do and trust Him in every step.",
    testid: "value-faith",
  },
  {
    icon: Leaf,
    title: "Purposeful",
    body: "Mindfully crafted to inspire wellness, beauty, and growth.",
    testid: "value-purposeful",
  },
  {
    icon: Hand,
    title: "Handmade with Care",
    body: "Each item is handcrafted with love, quality ingredients, and attention to detail.",
    testid: "value-handmade",
  },
  {
    icon: Users,
    title: "Community Focused",
    body: "We believe in supporting, uplifting, and giving back.",
    testid: "value-community",
  },
];

export default function AboutFounder() {
  return (
    <section
      data-testid="about-founder-section"
      id="about"
      className="relative bg-brand-cream py-24 lg:py-32 paper-grain overflow-hidden"
    >
      <IvyVine className="absolute -top-6 -left-6 w-44 opacity-50 hidden md:block" />
      <IvyVine className="absolute bottom-10 right-0 w-52 opacity-40 hidden md:block" flip />

      {/* Brand watercolor flower clusters */}
      <img src="/assets/flower_bluebell.png" alt="" aria-hidden="true" className="absolute -top-4 -left-6 w-56 lg:w-72 opacity-85 animate-float-slow pointer-events-none hidden md:block" />
      <img src="/assets/flower_rose.png" alt="" aria-hidden="true" className="absolute bottom-0 -left-8 w-56 lg:w-72 opacity-90 animate-drift pointer-events-none hidden md:block" />
      <img src="/assets/flower_cosmos.png" alt="" aria-hidden="true" className="absolute -top-2 -right-8 w-56 lg:w-72 opacity-90 animate-float-slow pointer-events-none hidden md:block" style={{ animationDelay: "0.8s" }} />
      <img src="/assets/flower_fern.png" alt="" aria-hidden="true" className="absolute bottom-0 -right-6 w-56 lg:w-72 opacity-85 animate-drift pointer-events-none hidden md:block" style={{ animationDelay: "0.4s" }} />

      {/* Small scattered flowers */}
      <FlowerRed className="absolute top-32 left-[18%] w-12 h-12 opacity-90 animate-float-slow z-10" />
      <FlowerYellow className="absolute top-48 right-[20%] w-10 h-10 opacity-90 animate-drift z-10" />
      <FlowerLavender className="absolute bottom-24 right-[18%] w-10 h-16 opacity-90 z-10" />
      <LeafSprig className="absolute top-72 right-[6%] w-24 opacity-50 z-10" />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-12 z-20 grid lg:grid-cols-12 gap-12 lg:gap-16 items-start">
        {/* Founder portrait + identity card (left) */}
        <div className="lg:col-span-5">
          <div className="relative w-full max-w-[440px] mx-auto">
            <div className="absolute -inset-3 rounded-[2rem] border-[6px] border-[#C68465]/70 rotate-[-2deg]" />
            <div className="relative rounded-[1.6rem] overflow-hidden aspect-[245/430] shadow-2xl">
              <img
                src="/assets/founder_chatoria_v2.png"
                alt="Chatoria N. Franklin, founder of 414, LLC"
                data-testid="founder-portrait"
                className="w-full h-full object-cover"
                style={{ objectPosition: "center top" }}
              />
            </div>

            {/* Faith-Purpose-Intention badge */}
            <div className="absolute -top-6 -right-4 lg:-right-10 bg-brand-moss text-brand-cream rounded-full px-7 py-6 shadow-2xl border-2 border-brand-gold/60 text-center rotate-[3deg] w-[180px] z-10">
              <p className="text-[10px] uppercase tracking-[0.3em] text-brand-gold/90">Rooted in</p>
              <p className="font-script text-2xl text-[#E8B6A8] leading-tight">Faith <span className="text-brand-gold">♡</span></p>
              <p className="text-[10px] uppercase tracking-[0.3em] text-brand-gold/90 mt-2">Fueled by</p>
              <p className="font-script text-2xl text-[#E8B6A8] leading-tight">Purpose <span className="text-brand-gold">♡</span></p>
              <p className="text-[10px] uppercase tracking-[0.3em] text-brand-gold/90 mt-2">Growing with</p>
              <p className="font-script text-2xl text-[#E8B6A8] leading-tight">Intention</p>
            </div>
          </div>

          <div className="mt-10 text-center">
            <div className="inline-block bg-[#5D2138] text-brand-cream px-7 py-1.5 -rotate-1">
              <span className="text-xs uppercase tracking-[0.35em]">Meet the Founder</span>
            </div>
            <p className="mt-5 font-script text-5xl text-[#B7574E] leading-none" data-testid="founder-signature">
              Chatoria N. Franklin
            </p>
            <p className="mt-3 text-xs uppercase tracking-[0.4em] text-brand-moss/70">CEO &amp; Founder</p>
          </div>
        </div>

        {/* Mission + values (right) */}
        <div className="lg:col-span-7">
          <div className="inline-block bg-[#5D2138] text-brand-cream px-8 py-2 -rotate-1 shadow-sm mb-7">
            <span className="font-serif italic text-lg tracking-[0.2em] uppercase">Our Mission</span>
          </div>

          <p className="text-lg leading-relaxed text-brand-ink/85 max-w-2xl">
            At <span className="font-semibold text-[#B7574E]">414, LLC</span>, we create handcrafted products
            that bring beauty, comfort, wellness, and inspiration into everyday life. Through our
            floral planters, scented candles, and natural hair growth oils, we help transform spaces,
            encourage self-care, and nurture confidence from the inside out.
          </p>

          <div className="my-10 flex items-center gap-3 text-brand-gold">
            <span className="h-px w-16 bg-brand-gold/40" />
            <Heart className="w-4 h-4 fill-current" />
            <span className="h-px w-16 bg-brand-gold/40" />
          </div>

          <div className="inline-block bg-[#5D2138] text-brand-cream px-8 py-2 -rotate-1 shadow-sm mb-8">
            <span className="font-serif italic text-lg tracking-[0.2em] uppercase">Our Values</span>
          </div>

          <div className="grid sm:grid-cols-2 gap-5">
            {values.map((v) => {
              const Icon = v.icon;
              return (
                <div
                  key={v.testid}
                  data-testid={v.testid}
                  className="flex gap-4 group"
                >
                  <span className="flex-shrink-0 flex items-center justify-center w-12 h-12 rounded-full bg-brand-moss text-brand-cream transition-transform group-hover:scale-105">
                    <Icon className="w-5 h-5" strokeWidth={1.6} />
                  </span>
                  <div>
                    <h3 className="font-sans text-sm uppercase tracking-[0.18em] text-brand-moss font-semibold">
                      {v.title}
                    </h3>
                    <p className="mt-1 text-sm leading-relaxed text-brand-ink/75">
                      {v.body}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>

          <p className="mt-12 font-serif italic text-2xl text-[#B7574E] text-center lg:text-left leading-relaxed">
            Cultivating Beauty.<br className="sm:hidden" /> Inspiring Wellness. Creating with Purpose. <span className="text-brand-gold">♡</span>
          </p>
        </div>
      </div>
    </section>
  );
}
